import { useCallback, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { buildBetTaskReferenceMap } from "./betSubmissionMappers";
import { useGetUserEnrollments } from "./useGetUserEnrollments";
import { useGetUserProgressBySeasonIds } from "./useGetUserProgressBySeasonIds";

type UseBetGamePassSubmissionsOptions = {
  /** Gate: while false, no enrollments/progress requests are issued. */
  enabled?: boolean;
};

type UseBetGamePassSubmissionsResult = {
  /** bet_id → "Task <challenge>.<task>" for every redeemed Game Pass bet. */
  taskReferencesByBetId: ReadonlyMap<string, string>;
  isLoading: boolean;
  /**
   * FE-2330: invalidates the enrollments + per-season progress caches so the
   * submitted/not-submitted state refetches without a full page reload (e.g.
   * when an agent searches a bet id).
   */
  refresh: () => void;
};

/**
 * FE-2300: resolves which of a user's sportsbook bets were submitted toward a
 * Game Pass task. Walks the user's season enrollments, loads per-season
 * progress (shared react-query cache with the Game Pass tab), and reverse-maps
 * each task's redeemed bet ids to a "Task 1.2"-style reference.
 */
export const useBetGamePassSubmissions = (
  userId?: string,
  options?: UseBetGamePassSubmissionsOptions,
): UseBetGamePassSubmissionsResult => {
  const enabled = options?.enabled ?? true;
  const queryClient = useQueryClient();
  const enrollmentsQuery = useGetUserEnrollments(userId, { enabled });

  const seasonIds = useMemo(
    () =>
      (enrollmentsQuery.data ?? [])
        .map((record) => record.season_id ?? record.id)
        .filter(
          (seasonId): seasonId is string =>
            typeof seasonId === "string" && seasonId.trim() !== "",
        ),
    [enrollmentsQuery.data],
  );

  const { seasonProgressById, isLoading: isProgressLoading } =
    useGetUserProgressBySeasonIds(enabled ? userId : undefined, seasonIds);

  const taskReferencesByBetId = useMemo(
    () => buildBetTaskReferenceMap(seasonProgressById),
    [seasonProgressById],
  );

  const refresh = useCallback(() => {
    if (!userId) return;
    // Prefix match: the progress key carries the season id as a fifth
    // element, so this invalidates every enrolled season at once.
    queryClient.invalidateQueries({
      queryKey: ["game-pass", "users", "enrollments", userId],
    });
    queryClient.invalidateQueries({
      queryKey: ["game-pass", "users", "progress", userId],
    });
  }, [queryClient, userId]);

  return {
    taskReferencesByBetId,
    isLoading: enabled && (enrollmentsQuery.isLoading || isProgressLoading),
    refresh,
  };
};
